import { useCallback, useEffect, useRef } from 'react'

interface MapPolylineOverlayProps {
  map: any
  path: google.maps.LatLngLiteral[]
  options: google.maps.PolylineOptions
  onClick?: (event: google.maps.PolyMouseEvent) => void
  onPathChanged?: (path: google.maps.LatLngLiteral[]) => void
}

interface MapPolygonOverlayProps {
  map: any
  paths: google.maps.LatLngLiteral[]
  options: google.maps.PolygonOptions
  onClick?: (event: google.maps.MapMouseEvent) => void
  onDoubleClick?: (event: google.maps.MapMouseEvent) => void
  onPathChanged?: (path: google.maps.LatLngLiteral[]) => void
}

interface MapAdvancedMarkerProps {
  map: any
  position: google.maps.LatLngLiteral
  draggable?: boolean
  zIndex?: number
  anchorLeft?: string
  anchorTop?: string
  buildContent: () => HTMLElement
  onClick?: (event: any) => void
  onHostClick?: (event: MouseEvent) => void
  onDoubleClick?: (event: MouseEvent) => void
  onDragStart?: (event: any) => void
  onDrag?: (event: any) => void
  onDragEnd?: (event: any) => void
  onLoad?: (marker: google.maps.marker.AdvancedMarkerElement) => void
  onUnmount?: (marker: google.maps.marker.AdvancedMarkerElement) => void
}

/**
 * Explicit Google Maps polyline lifecycle management.
 * Rather than relying on the default Polyline component which can have issues dev mode,
 * this component directly manages the Google Maps Polyline instance and its lifecycle
 * to ensure cleaning up the old polylines on React strict mode remount.
 */
export const MapPolylineOverlay = ({
  map,
  path,
  options,
  onClick,
  onPathChanged,
}: MapPolylineOverlayProps): JSX.Element | null => {
  const polylineRef = useRef<google.maps.Polyline | null>(null)
  const pathListenersRef = useRef<google.maps.MapsEventListener[]>([])
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null)
  const pathSignatureRef = useRef<string>('[]')

  useEffect(() => {
    if (!map || !window.google?.maps) {
      return
    }

    const polyline = new window.google.maps.Polyline({
      ...options,
      path,
      map,
    })

    polylineRef.current = polyline

    return () => {
      pathListenersRef.current.forEach((listener) => window.google.maps.event.removeListener(listener))
      pathListenersRef.current = []
      if (clickListenerRef.current) {
        window.google.maps.event.removeListener(clickListenerRef.current)
        clickListenerRef.current = null
      }
      polyline.setMap(null)
      polylineRef.current = null
    }
  }, [map])

  useEffect(() => {
    if (!polylineRef.current) {
      return
    }

    const polyline = polylineRef.current
    const pathSignature = JSON.stringify(path)
    pathSignatureRef.current = pathSignature

    polyline.setOptions(options)
    polyline.setPath(path)

    if (map) {
      polyline.setMap(map)
    }

    pathListenersRef.current.forEach((listener) => window.google.maps.event.removeListener(listener))
    pathListenersRef.current = []

    if (clickListenerRef.current) {
      window.google.maps.event.removeListener(clickListenerRef.current)
      clickListenerRef.current = null
    }

    if (onClick) {
      clickListenerRef.current = polyline.addListener('click', onClick)
    }

    if (onPathChanged) {
      const emitPathChange = () => {
        if (!polylineRef.current) {
          return
        }

        const nextPath = polylineRef.current
          .getPath()
          .getArray()
          .map((point) => ({ lat: point.lat(), lng: point.lng() }))
        const nextSignature = JSON.stringify(nextPath)

        if (nextSignature === pathSignatureRef.current) {
          return
        }

        pathSignatureRef.current = nextSignature
        onPathChanged(nextPath)
      }

      const pathMvcArray = polyline.getPath()
      pathListenersRef.current = [
        pathMvcArray.addListener('set_at', emitPathChange),
        pathMvcArray.addListener('insert_at', emitPathChange),
        pathMvcArray.addListener('remove_at', emitPathChange),
      ]
    }
  }, [map, onClick, onPathChanged, options, path])

  return null
}

/**
 * Explicit Google Maps polygon lifecycle management.
 * This avoids stale polygon instances in React strict mode remounts by managing
 * creation, updates, listeners, and cleanup manually.
 */
export const MapPolygonOverlay = ({
  map,
  paths,
  options,
  onClick,
  onDoubleClick,
  onPathChanged,
}: MapPolygonOverlayProps): JSX.Element | null => {
  const polygonRef = useRef<google.maps.Polygon | null>(null)
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null)
  const doubleClickListenerRef = useRef<google.maps.MapsEventListener | null>(null)
  const pathListenersRef = useRef<google.maps.MapsEventListener[]>([])
  const pathSignatureRef = useRef<string>('[]')

  useEffect(() => {
    if (!map || !window.google?.maps) {
      return
    }

    const polygon = new window.google.maps.Polygon({ map })
    polygonRef.current = polygon

    return () => {
      if (clickListenerRef.current) {
        window.google.maps.event.removeListener(clickListenerRef.current)
        clickListenerRef.current = null
      }
      if (doubleClickListenerRef.current) {
        window.google.maps.event.removeListener(doubleClickListenerRef.current)
        doubleClickListenerRef.current = null
      }
      pathListenersRef.current.forEach((listener) => window.google.maps.event.removeListener(listener))
      pathListenersRef.current = []
      polygon.setMap(null)
      polygonRef.current = null
    }
  }, [map])

  useEffect(() => {
    if (!polygonRef.current || !window.google?.maps) {
      return
    }

    const polygon = polygonRef.current
    const pathSignature = JSON.stringify(paths)
    pathSignatureRef.current = pathSignature

    polygon.setOptions(options)
    polygon.setPath(paths)

    if (map) {
      polygon.setMap(map)
    }

    if (clickListenerRef.current) {
      window.google.maps.event.removeListener(clickListenerRef.current)
      clickListenerRef.current = null
    }

    if (onClick) {
      clickListenerRef.current = polygon.addListener('click', onClick)
    }

    if (doubleClickListenerRef.current) {
      window.google.maps.event.removeListener(doubleClickListenerRef.current)
      doubleClickListenerRef.current = null
    }

    if (onDoubleClick) {
      doubleClickListenerRef.current = polygon.addListener('dblclick', onDoubleClick)
    }

    pathListenersRef.current.forEach((listener) => window.google.maps.event.removeListener(listener))
    pathListenersRef.current = []

    if (onPathChanged) {
      const emitPathChange = () => {
        if (!polygonRef.current) {
          return
        }

        const nextPath = polygonRef.current
          .getPath()
          .getArray()
          .map((point) => ({ lat: point.lat(), lng: point.lng() }))
        const nextSignature = JSON.stringify(nextPath)

        if (nextSignature === pathSignatureRef.current) {
          return
        }

        pathSignatureRef.current = nextSignature
        onPathChanged(nextPath)
      }

      const pathMvcArray = polygon.getPath()
      pathListenersRef.current = [
        pathMvcArray.addListener('set_at', emitPathChange),
        pathMvcArray.addListener('insert_at', emitPathChange),
        pathMvcArray.addListener('remove_at', emitPathChange),
      ]
    }
  }, [map, onClick, onDoubleClick, onPathChanged, options, paths])

  return null
}

export const MapAdvancedMarker = ({
  map,
  position,
  draggable,
  zIndex,
  anchorLeft,
  anchorTop,
  buildContent,
  onClick,
  onHostClick,
  onDoubleClick,
  onDragStart,
  onDrag,
  onDragEnd,
  onLoad,
  onUnmount,
}: MapAdvancedMarkerProps): JSX.Element | null => {
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null)
  const listenersRef = useRef<google.maps.MapsEventListener[]>([])
  const doubleClickCleanupRef = useRef<(() => void) | null>(null)
  const hostClickCleanupRef = useRef<(() => void) | null>(null)

  const applyMarkerHostStyles = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement) => {
      const host = marker as any

      host.crossOnDrag = false
      host.style.outline = 'none'
      host.style.boxShadow = 'none'
      host.style.webkitTapHighlightColor = 'transparent'
      host.classList?.add('gps-editor-advanced-marker-host')

      if (draggable) {
        host.classList?.add('gps-editor-advanced-marker-host-draggable')
      } else {
        host.classList?.remove('gps-editor-advanced-marker-host-draggable')
      }
    },
    [draggable],
  )

  const applyMarkerAnchor = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement) => {
      ;(marker as any).anchorLeft = anchorLeft
      ;(marker as any).anchorTop = anchorTop
    },
    [anchorLeft, anchorTop],
  )

  useEffect(() => {
    if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) {
      return
    }

    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      map,
      position,
      gmpDraggable: Boolean(draggable),
      gmpClickable: Boolean(onClick),
      zIndex,
    })

    applyMarkerAnchor(marker)
    applyMarkerHostStyles(marker)
    marker.append(buildContent())

    markerRef.current = marker
    onLoad?.(marker)

    return () => {
      listenersRef.current.forEach((listener) => window.google.maps.event.removeListener(listener))
      listenersRef.current = []
      doubleClickCleanupRef.current?.()
      doubleClickCleanupRef.current = null
      hostClickCleanupRef.current?.()
      hostClickCleanupRef.current = null

      if (markerRef.current) {
        onUnmount?.(markerRef.current)
        markerRef.current.map = null
      }

      markerRef.current = null
    }
  }, [applyMarkerAnchor, applyMarkerHostStyles, draggable, map, onClick])

  useEffect(() => {
    const marker = markerRef.current
    if (!marker) {
      return
    }

    marker.position = position
    marker.zIndex = zIndex
    marker.gmpDraggable = Boolean(draggable)
    marker.gmpClickable = Boolean(onClick)
    applyMarkerAnchor(marker)
    applyMarkerHostStyles(marker)
    marker.replaceChildren(buildContent())

    listenersRef.current.forEach((listener) => window.google.maps.event.removeListener(listener))
    listenersRef.current = []

    if (onClick) {
      listenersRef.current.push(marker.addListener('gmp-click', onClick))
    }

    if (onDragStart) {
      listenersRef.current.push(marker.addListener('dragstart', onDragStart))
    }

    if (onDrag) {
      listenersRef.current.push(marker.addListener('drag', onDrag))
    }

    if (onDragEnd) {
      listenersRef.current.push(marker.addListener('dragend', onDragEnd))
    }

    doubleClickCleanupRef.current?.()
    doubleClickCleanupRef.current = null

    if (onDoubleClick) {
      const handleDoubleClick = (event: MouseEvent) => {
        event.stopPropagation()
        onDoubleClick(event)
      }
      const host = marker as unknown as HTMLElement
      host.addEventListener('dblclick', handleDoubleClick)
      doubleClickCleanupRef.current = () => host.removeEventListener('dblclick', handleDoubleClick)
    }

    hostClickCleanupRef.current?.()
    hostClickCleanupRef.current = null

    if (onHostClick) {
      const host = marker as unknown as HTMLElement
      const handleHostClick = (event: MouseEvent) => {
        onHostClick(event)
      }
      host.addEventListener('click', handleHostClick)
      hostClickCleanupRef.current = () => host.removeEventListener('click', handleHostClick)
    }
  }, [
    applyMarkerAnchor,
    applyMarkerHostStyles,
    buildContent,
    draggable,
    onClick,
    onHostClick,
    onDoubleClick,
    onDrag,
    onDragEnd,
    onDragStart,
    position,
    zIndex,
  ])

  return null
}
